import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Check,
  ArrowRight,
  Brain,
  Lock,
  BarChart3,
  FileText,
  Monitor,
  Users,
  Building2,
  Key,
} from "lucide-react";
import PublicHeader from "../PublicHeader/PublicHeader";
import Footer from "../Footer/Footer";
import SolutionsFAQ from "../SolutionsFAQ/SolutionsFAQ";
import ReadyToTransform from "../ReadyToTransform/ReadyToTransform";

const SolutionsPageTemplate = ({
  // Breadcrumb
  breadcrumbTitle,
  // Hero Section
  heroTitle,
  heroSubtitle,
  heroTextCenter = true, // When false (e.g. Remote Teams), hero title/subtitle stay left-aligned
  primaryButtonText = "Get a Demo",
  primaryButtonIcon = Sparkles,
  customPrimaryButton = null, // Custom React element for primary button (overrides default)
  // Key Productivity Areas
  productivityTitle = "Key Productivity Areas Remote Teams Need Visibility Into",
  productivitySubtitle = "See how high-performing remote teams track work, manage time, and stay connected—without friction.",
  productivityCards = [], // Array of 4 cards with {title, description, points: []}
  productivityCardIcons = null, // Optional: array of icon image src for each productivity card
  productivityCardAccentColors = null, // Optional: array of accent colors (css color) for each productivity card
  // Complete Feature Suite
  featureSuiteTitle = "Complete Feature Suite for Remote Teams",
  featureSuiteSubtitle = "Everything you need in EdTech productivity software",
  customFeatures = null, // If provided, will use custom features instead of common ones
  hideFeatures = false, // If true, hide the feature suite section
  // Outcomes
  outcomesTitle = "Outcomes You Can Expect",
  outcomesSubtitle = null,
  customOutcomes = null, // If provided, will use custom outcomes instead of common ones
  hideOutcomes = false, // If true, hide the outcomes section
  // Why Choose Section
  whyChooseTitle = "Why Remote Teams Choose TeamGrid",
  whyChooseDescription = "TeamGrid is more than a remote team time tracking software. It's a productivity intelligence platform for remote and distributed teams, built to improve delivery, engagement, and focus without micromanagement.",
  whyChooseButtonText = "See TeamGrid in Action",
  hideWhyChoose = false, // If true, hide the why choose section
  // Custom Full Solution Content Section
  fullSolutionContentTitle = null, // If provided, shows a custom content section
  fullSolutionContentDescription = null,
  fullSolutionContentOutcomes = null,
  // FAQ
  faqs = [],
  faqTitle = "Frequently Asked Questions",
  faqSubtitle = "Everything you need to know about TeamGrid for remote and distributed teams.",
  faqBackgroundColor = "#F3F4F6", // Optional: custom background color for FAQ section
}) => {
  const navigate = useNavigate();
  const PrimaryIcon = primaryButtonIcon;

  const defaultProductivityIcons = [
    "/Background_sol_pg_1.webp",
    "/Background.webp",
    "/Background_sol_pg_2.webp",
    "/Background_sol_pg_3.webp",
  ];

  const defaultProductivityAccentColors = [
    "#00D1C1", // teal
    "#3B82F6", // blue
    "#8B5CF6", // purple
    "#F97316", // orange
  ];

  // Common feature suite (same for all solutions)
  const commonFeatures = [
    {
      img: "/SVG_sol_1.webp",
      title: "AI-Powered Insights",
      desc: "Automatic analysis of remote work patterns.",
    },
    {
      img: "/SVG_sol_2.webp",
      title: "Privacy-First",
      desc: "No screenshots, keyloggers, or spy tactics.",
    },
    {
      img: "/SVG_sol_3.webp",
      title: "Daily Summaries",
      desc: "Auto-generated reports, no manual updates.",
    },
    {
      img: "/SVG_sol_4.webp",
      title: "Productivity Score",
      desc: "Real-time visibility into output velocity.",
    },
    {
      img: "/SVG_sol_5.webp",
      title: "App Pattern",
      desc: "Understand productive vs distracting tools.",
    },
    {
      img: "/SVG_sol_6.webp",
      title: "Team Management",
      desc: "Role-based visibility and project progress.",
    },
    {
      img: "/SVG_sol_7.webp",
      title: "Anti-Surveillance",
      desc: "Signal over surveillance for remote teams.",
    },
    {
      img: "/SVG_sol_8.webp",
      title: "Org Intelligence",
      desc: "Company-wide benchmarks and insights.",
    },
  ];

  // Common outcomes (same for all solutions)
  const commonOutcomes = [
    {
      percentage: "40%",
      title: "Faster Execution",
      desc: "Remote teams reduce meeting dependency.",
    },
    {
      percentage: "30%",
      title: "Stronger Connection",
      desc: "Early isolation detection enables proactive collaboration.",
    },
    {
      percentage: "35%",
      title: "Fewer Meetings",
      desc: "Balanced scheduling protects time and productivity.",
    },
    {
      percentage: "25%",
      title: "Higher Retention",
      desc: "Employees stay when productivity and trust coexist.",
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#FFFFFF] text-[#111827]">
      <PublicHeader />
      <div className="pt-16 sm:pt-18 md:pt-20">
        <div className="relative z-10">
          {/* Hero Section - HERO SECTION (Figma) */}
          <section
            className="flex flex-col justify-center items-center w-full py-8 sm:py-10 lg:py-12 px-4 sm:px-6 lg:px-8 flex-none"
            style={{
              background:
                "radial-gradient(141.42% 141.42% at 100% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 0% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
            }}
          >
            <div className="max-w-7xl w-full mx-auto flex flex-row items-center gap-16 flex-none self-stretch">
              {/* Left Text */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className={`flex flex-col gap-5 flex-1 min-w-0 ${heroTextCenter ? "items-center text-center" : "items-start"}`}
              >
                {/* Breadcrumb: Solutions → {breadcrumbTitle} */}
                <div
                  className={`flex flex-row items-center gap-2 self-stretch ${heroTextCenter ? "justify-center" : ""}`}
                >
                  <button
                    onClick={() => navigate("/solutions")}
                    className="flex items-center font-normal text-[13.7px] leading-[22px] text-black hover:underline"
                    style={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    Solutions
                  </button>
                  <span
                    className="flex items-center font-normal text-[14px] leading-[22px] text-black"
                    style={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    →
                  </span>
                  <span
                    className="flex items-center font-normal text-[14px] leading-[22px] text-black"
                    style={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    {breadcrumbTitle}
                  </span>
                </div>

                {/* Title + subtitle group — tighter gap so subtitle sits closer to title */}
                <div
                  className={`flex flex-col gap-2 ${heroTextCenter ? "items-center" : "items-start"}`}
                >
                  {/* Heading 1 — same size on all solution pages; 204px min-height + flex center only when centered (not Remote Teams) */}
                  <h1
                    className={`font-extrabold text-black self-stretch ${heroTextCenter ? "flex flex-col justify-center text-center items-center" : "items-start"}`}
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "clamp(2.5rem, 5vw, 62px)",
                      lineHeight: "clamp(50px, 8vw, 72px)",
                      padding: 0,
                      ...(heroTextCenter && {
                        textAlign: "center",
                        minHeight: 204,
                      }),
                    }}
                  >
                    {(() => {
                      const highlightPhrase = "Collaboration Insights";
                      if (heroTitle?.includes("<span")) {
                        return (
                          <span
                            dangerouslySetInnerHTML={{ __html: heroTitle }}
                          />
                        );
                      }
                      if (!heroTitle?.includes(highlightPhrase)) {
                        return heroTitle;
                      }
                      const [before] = heroTitle.split(highlightPhrase);
                      return (
                        <>
                          {before}
                          <span className="text-[#00D1C1] font-semibold">
                            {highlightPhrase}
                          </span>
                        </>
                      );
                    })()}
                  </h1>

                  {/* Subtitle — single line on all solution pages except Remote Teams */}
                  <p
                    className={`font-normal text-[#475569] px-4 sm:px-0 ${heroTextCenter ? "mx-auto text-center" : "max-w-[512px]"}`}
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "clamp(16px, 4vw, 20px)",
                      lineHeight: "clamp(24px, 6vw, 29px)",
                      ...(heroTextCenter && { textAlign: "center" }),
                    }}
                  >
                    {heroSubtitle}
                  </p>
                </div>

                {/* CTA Buttons */}
                <div
                  className={`flex flex-row items-center gap-4 self-stretch ${heroTextCenter ? "justify-center" : "items-start"}`}
                >
                  {customPrimaryButton || (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/book-demo")}
                      className="flex items-center justify-center py-4 px-8 rounded-xl font-bold text-[18px] leading-[28px] text-black text-center min-h-[62px]"
                      style={{
                        background: "#00D1C1",
                        boxShadow:
                          "0px 10px 15px -3px rgba(0, 209, 193, 0.2), 0px 4px 6px -4px rgba(0, 209, 193, 0.2)",
                      }}
                    >
                      Get a Demo
                    </motion.button>
                  )}
                </div>
              </motion.div>

              {/* Right: Global Alignment card (static UI) - 608×414 per design */}
              <div className="hidden md:block">
                {breadcrumbTitle === "Remote Teams" && (
                  <div className="w-[608px] flex flex-col items-start p-0 flex-none rounded-3xl bg-[#F9FBFF] shadow-[0_24px_80px_rgba(15,23,42,0.10)] border border-[#E5EDF9] overflow-hidden">
                    <div className="w-full px-6 py-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm font-semibold text-[#111827]">
                          Global Alignment
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                            LIVE
                          </span>
                          <span className="text-gray-500">SYNC</span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        {/* Row 1 */}
                        <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-slate-50 text-slate-500 text-xs font-semibold flex items-center justify-center">
                              LDN
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-[#111827]">
                                Design Team
                              </p>
                              <p className="text-xs text-gray-500">
                                London · 2:30 PM
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-semibold text-emerald-600">
                                High Focus
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Row 2 */}
                        <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-sky-50 text-sky-500 text-xs font-semibold flex items-center justify-center">
                              NYC
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-[#111827]">
                                Engineering
                              </p>
                              <p className="text-xs text-gray-500">
                                New York · 9:30 AM
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] font-semibold text-gray-600">
                                Standup
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Row 3 */}
                        <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3 opacity-80">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-rose-50 text-rose-400 text-xs font-semibold flex items-center justify-center">
                              TKY
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-[#111827]">
                                QA Team
                              </p>
                              <p className="text-xs text-gray-500">
                                Tokyo · 10:30 PM
                              </p>
                            </div>
                          </div>
                          <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-[11px] font-semibold text-gray-400">
                            OFFLINE
                          </span>
                        </div>
                      </div>

                      {/* Insight Box - at bottom of card */}
                      <div
                        className="w-full mt-4 rounded-2xl flex items-start gap-3 px-4 py-4"
                        style={{
                          background: "#E0FFFC",
                          border: "1px solid rgba(0, 209, 193, 0.2)",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        <img
                          src="/SVG_New90.webp"
                          alt="Collaboration window"
                          className="h-5 w-5 flex-shrink-0 mt-0.5 object-contain"
                          aria-hidden="true"
                        />
                        <p className="text-sm leading-relaxed text-[#134E4A]">
                          <span className="font-semibold">
                            Collaboration Window:
                          </span>{" "}
                          Best time for cross-team sync is{" "}
                          <span className="font-semibold">
                            9:00 AM – 11:00 AM EST.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Key Productivity Areas Section */}
          {productivityCards.length > 0 && (
            <section
              className="py-4 sm:py-6 lg:py-8 !bg-white"
              style={{ backgroundColor: "#FFFFFF" }}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-col items-center gap-5 p-0 mb-8 w-full max-w-[1232px] min-h-[80px] mx-auto text-center"
                >
                  <h2 className="font-inter font-extrabold text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] leading-tight tracking-normal text-[#111827] max-w-[608px] mx-auto">
                    {productivityTitle}
                  </h2>
                  <p className="text-[15px] md:text-[16px] text-[#6B7280]">
                    {productivitySubtitle}
                  </p>
                </motion.div>

                {/* Four Cards in 2x2 Grid */}
                <div className="grid md:grid-cols-2 gap-8">
                  {productivityCards.map((card, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 + idx * 0.1 }}
                      className="relative bg-white border border-[#E5E7EB] rounded-[24px] p-8 pt-14 shadow-[0_24px_60px_rgba(15,23,42,0.04)]"
                    >
                      <img
                        src={
                          (productivityCardIcons || defaultProductivityIcons)[
                            idx
                          ]
                        }
                        alt={card.title}
                        className="absolute top-6 left-6 w-12 h-12 object-contain"
                        aria-hidden="true"
                      />
                      <h3 className="mt-3 text-lg md:text-xl font-bold text-[#111827] mb-2">
                        {card.title}
                      </h3>
                      <p className="text-sm text-[#6B7280] italic mb-5 leading-relaxed">
                        {card.description}
                      </p>
                      <ul className="space-y-3">
                        {card.points.map((point, pointIdx) => (
                          <li key={pointIdx} className="flex items-start gap-3">
                            <Check
                              className="w-4 h-4 flex-shrink-0 mt-0.5"
                              style={{
                                color: (productivityCardAccentColors ||
                                  defaultProductivityAccentColors)[idx],
                              }}
                            />
                            <span className="text-sm text-[#4B5563] leading-relaxed">
                              {point}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Full Solution Content Section */}
          {fullSolutionContentTitle && (
            <section className="py-6 sm:py-8 lg:py-10 bg-[#FFFFFF]">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="mb-8 max-w-[1200px]"
                >
                  <h2 className="font-inter font-extrabold text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] leading-tight tracking-normal text-[#111827] mb-6">
                    {fullSolutionContentTitle}
                  </h2>
                  {fullSolutionContentDescription && (
                    <p className="text-sm md:text-base text-gray-600 mb-6 leading-snug max-w-[1200px]">
                      {fullSolutionContentDescription}
                    </p>
                  )}
                  {fullSolutionContentOutcomes && (
                    <p className="text-sm md:text-base text-gray-600 leading-snug max-w-[1200px]">
                      {fullSolutionContentOutcomes}
                    </p>
                  )}
                </motion.div>
              </div>
            </section>
          )}

          {/* Complete Feature Suite Section - Bento Grid */}
          {!hideFeatures && (
            <section
              className="flex flex-col justify-center items-center py-6 sm:py-8 lg:py-10 !bg-white"
              style={{ backgroundColor: "#FFFFFF" }}
            >
              <div className="flex flex-col items-center w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 gap-10">
                {/* Heading + subtitle - same on all solution pages */}
                <div className="flex flex-col items-center w-full gap-5 max-w-[1232px] mx-auto">
                  <h2 className="font-inter font-extrabold text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] leading-tight tracking-normal text-[#111827] text-center w-full max-w-[608px]">
                    {featureSuiteTitle}
                  </h2>
                  <p
                    className="text-center w-full text-[#475569] max-w-3xl"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      fontSize: 20,
                      lineHeight: "24px",
                    }}
                  >
                    {featureSuiteSubtitle}
                  </p>
                </div>

                {/* Feature Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
                  {(customFeatures || commonFeatures).map((feature, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 + idx * 0.1 }}
                      className="bg-white border border-[#EDF4FF] rounded-xl p-6 transition-all"
                      style={{
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <div className="mb-4">
                        {feature.img ? (
                          <img
                            src={feature.img}
                            alt={feature.title}
                            className="w-8 h-8"
                          />
                        ) : feature.icon ? (
                          <feature.icon
                            className={`w-8 h-8 ${feature.iconColor || "text-teal-400"}`}
                          />
                        ) : null}
                      </div>
                      <h3
                        className="font-bold text-[#000000] mb-1.5 whitespace-pre-line"
                        style={{ fontSize: 14, lineHeight: "20px" }}
                      >
                        {feature.title}
                      </h3>
                      <p
                        className="text-[#475569] line-clamp-2"
                        style={{ fontSize: 12, lineHeight: "16px" }}
                      >
                        {feature.desc}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Outcomes Section */}
          {!hideOutcomes && (
            <section
              className="py-6 sm:py-8 lg:py-10"
              style={{ backgroundColor: "#F5FEFC" }}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.6 }}
                  className="mb-6 text-center"
                >
                  <h2 className="font-inter font-extrabold text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] leading-tight tracking-normal text-[#111827] max-w-[608px] mx-auto">
                    {outcomesTitle}
                  </h2>
                  {outcomesSubtitle && (
                    <p className="mt-3 text-[15px] md:text-[16px] text-black">
                      {outcomesSubtitle}
                    </p>
                  )}
                </motion.div>

                {/* Outcome metrics row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center">
                  {(customOutcomes || commonOutcomes).map((outcome, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 1.7 + idx * 0.1 }}
                      className="flex flex-col items-center"
                    >
                      <div
                        className="text-3xl md:text-4xl font-extrabold mb-1"
                        style={{ color: "#00D1C1" }}
                      >
                        {outcome.percentage}
                      </div>
                      <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-[#000000] mb-1.5">
                        {outcome.title}
                      </div>
                      <p className="text-[12px] leading-[16px] text-black max-w-[200px]">
                        {outcome.desc}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Why Remote Teams Choose TeamGrid Section */}
          {!hideWhyChoose && (
            <section className="ready-cta-section mt-0 mb-[-1px] py-6 sm:py-8 lg:py-10 md:py-12 bg-black text-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 2.1 }}
                  className="rounded-2xl p-8 md:p-10 lg:p-12"
                  style={{
                    background: "#000000",
                  }}
                >
                  <h2 className="font-inter font-extrabold text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] leading-tight tracking-normal text-white/90 mb-3 md:mb-4 text-center">
                    {whyChooseTitle}
                  </h2>
                  <p className="text-[15px] md:text-[16px] text-white/90 mb-4 md:mb-5 leading-relaxed max-w-[800px] mx-auto text-center">
                    {whyChooseDescription}
                  </p>
                  <div className="flex justify-center">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/book-demo")}
                      className="px-10 py-4 rounded-lg font-semibold text-white text-lg transition-all"
                      style={{
                        background: "#00D1C1",
                        boxShadow: "0px 8px 32px rgba(0, 209, 193, 0.6)",
                      }}
                    >
                      {whyChooseButtonText || "See TeamGrid in Action"}
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </section>
          )}
        </div>

        {/* FAQ Section - full-bleed gray bg (match Feature pages) */}
        {faqs.length > 0 && (
          <div
            className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-8 sm:py-12 lg:py-16"
            style={{ backgroundColor: faqBackgroundColor }}
          >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <SolutionsFAQ
                faqs={faqs}
                title={faqTitle}
                subtitle={faqSubtitle}
              />
            </div>
          </div>
        )}

        {/* Ready to Transform Section */}
        <ReadyToTransform />

        <Footer />
      </div>
    </div>
  );
};

export default SolutionsPageTemplate;
